<template>
  <teleport to="body">
    <!-- Fade-in is a CSS animation, not a <transition>: Vue's JS-driven enter
         never completed (element stuck at opacity 0 with the enter classes)
         when this modal was opened from another modal's `closed` handler. -->
    <div v-if="visible" class="vm-overlay" @mousedown.self="requestClose('overlay')">
      <!-- `v--modal-box` is vue-js-modal's box class: the legacy styles in
           assets/style/elements/_modal.scss (heading padding, close button,
           scrollable lists) key off it. -->
      <div class="vm-container v--modal-box" :class="{ 'vm-scrollable': scrollable }" :style="containerStyles" :data-modal="name" role="dialog">
        <slot />
      </div>
    </div>
  </teleport>
</template>

<script>
// Local Vue 3 replacement for vue-js-modal's <modal> (the API surface this
// app uses: name registry, $modal.show/hide, width/height/:adaptive/
// :resizable/scrollable props, closed / before-close events).
import { modalBus } from './bus';

export default {
  name: 'VmModal',
  props: {
    name: { type: String, required: true },
    width: { type: [Number, String], default: 600 },
    height: { type: [Number, String], default: 'auto' },
    scrollable: { type: Boolean, default: false },
    adaptive: { type: Boolean, default: false },   // accepted for compat; container is responsive anyway
    resizable: { type: Boolean, default: false },  // accepted for compat; not implemented
    clickToClose: { type: Boolean, default: true },
  },
  emits: ['closed', 'before-close', 'before-open', 'opened'],
  data() {
    return { visible: false };
  },
  computed: {
    containerStyles() {
      const dim = (v) => (typeof v === 'number' ? `${v}px` : v);
      return {
        width: dim(this.width),
        maxWidth: 'calc(100vw - 2rem)',
        height: this.height === 'auto' ? 'auto' : dim(this.height),
        maxHeight: 'calc(100vh - 2rem)',
      };
    },
  },
  methods: {
    open() {
      this.$emit('before-open');
      this.visible = true;
      this.$emit('opened');
    },
    requestClose(source) {
      if (source === 'overlay' && !this.clickToClose) return;
      let stopped = false;
      this.$emit('before-close', { stop: () => { stopped = true; } });
      if (stopped) return;
      this.visible = false;
      this.$emit('closed');
    },
    onKeyup(e) {
      if (e.key === 'Escape' && this.visible) this.requestClose('escape');
    },
    onBus({ name, action }) {
      if (name !== this.name) return;
      if (action === 'show') this.open();
      if (action === 'hide' && this.visible) this.requestClose('api');
    },
  },
  mounted() {
    modalBus.on('modal', this.onBus);
    document.addEventListener('keyup', this.onKeyup);
  },
  beforeUnmount() {
    modalBus.off('modal', this.onBus);
    document.removeEventListener('keyup', this.onKeyup);
  },
};
</script>

<style>
.vm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vm-container {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.vm-container.vm-scrollable {
  overflow-y: auto;
}
@keyframes vm-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.vm-overlay {
  animation: vm-fade-in 0.15s ease;
}
</style>
